/**
 * FiasArcheRolesCard — which Fias-owned plugin the platform's own links open
 * for each job (ADR-037 D8). The first role is the creative studio: "Open
 * FiasWorks Studio" on the forge page, and "Make one in FiasWorks Studio"
 * wherever a Creative Library comes up empty.
 *
 * A plugin's id differs per environment, so each environment is assigned here
 * rather than in code. Only a Fias-owned plugin can hold a role (the server
 * refuses any other), and a role grants the arche nothing — it is only where
 * the links go. Clearing a role hides its links.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type FiasArcheRoleAssignment } from '@/lib/api';
import {
  useFiasArcheRoles,
  usePlatformOwnedPlugins,
  useSetFiasArcheRole,
} from '@/contributor/hooks/usePlatformOwnedPlugins';

/** Fias owns a handful of plugins; one page covers them. */
const PLUGIN_PAGE = 100;

function saveErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'NOT_PLATFORM_OWNED') return 'Only a Fias-owned plugin can hold a role.';
    if (error.code === 'NOT_FOUND') return 'That arche no longer exists.';
  }
  return error instanceof Error ? error.message : 'The role was not saved.';
}

function RoleRow({
  assignment,
  plugins,
}: {
  assignment: FiasArcheRoleAssignment;
  plugins: Array<{ archeId: string; name: string }>;
}) {
  const save = useSetFiasArcheRole();
  const current = assignment.arche?.archeId ?? '';
  const [choice, setChoice] = React.useState(current);
  React.useEffect(() => setChoice(current), [current]);
  const selectId = `fias-arche-role-${assignment.role}`;
  const live =
    assignment.arche?.status === 'published' && assignment.arche.lifecycleState === 'active';

  return (
    <div className="space-y-2" data-testid={`fias-arche-role-${assignment.role}`}>
      <div>
        <p className="text-sm font-medium">{assignment.label}</p>
        <p className="text-xs text-muted-foreground">{assignment.description}</p>
      </div>
      {assignment.arche ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Links open </span>
          <span className="font-medium">{assignment.arche.name}</span>
          {!live && (
            <span className="text-destructive">
              {' '}
              — not published and active, so most people see no link
            </span>
          )}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Not assigned: the platform shows no link.</p>
      )}
      <div className="flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1">
          <Label htmlFor={selectId}>Fias plugin</Label>
          <select
            id={selectId}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={save.isPending}
          >
            <option value="">Choose a Fias plugin…</option>
            {plugins.map((p) => (
              <option key={p.archeId} value={p.archeId}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <Button
          disabled={!choice || choice === current || save.isPending}
          onClick={() => save.mutate({ role: assignment.role, archeId: choice })}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {assignment.arche && (
          <Button
            variant="outline"
            disabled={save.isPending}
            onClick={() => save.mutate({ role: assignment.role, archeId: null })}
          >
            Clear
          </Button>
        )}
      </div>
      {save.isError && (
        <p className="text-sm text-destructive" role="alert">
          {saveErrorText(save.error)}
        </p>
      )}
    </div>
  );
}

export function FiasArcheRolesCard() {
  const roles = useFiasArcheRoles();
  const plugins = usePlatformOwnedPlugins({ limit: PLUGIN_PAGE, offset: 0 });
  const options = (plugins.data?.arches ?? []).map((a) => ({ archeId: a.archeId, name: a.name }));

  return (
    <Card data-testid="fias-arche-roles-card">
      <CardHeader>
        <CardTitle>Platform links</CardTitle>
        <CardDescription>
          The Fias plugin the platform&apos;s own links open in this environment. A role gives the
          plugin nothing; it is only where the links go.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {roles.isLoading ? (
          <Skeleton className="h-5 w-64" />
        ) : roles.isError ? (
          <p className="text-sm text-destructive">
            Could not load the roles:{' '}
            {roles.error instanceof Error ? roles.error.message : 'unknown error'}
          </p>
        ) : (
          (roles.data ?? []).map((assignment) => (
            <RoleRow key={assignment.role} assignment={assignment} plugins={options} />
          ))
        )}
      </CardContent>
    </Card>
  );
}
